import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
    ConfigProvider,
    Layout,
    Empty,
    Button,
    Badge,
    Dropdown,
    Space,
    Tooltip,
    notification,
    Typography,
    Spin,
    message,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ProTable, BetaSchemaForm } from "@ant-design/pro-components";

import _ from "lodash";
import moment from "moment";
import ptBRIntl from "antd/lib/locale/pt_BR";
import uuid from "react-uuid";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";

/**
 * Actions
 */
import {
    addRefFieldsFormData,
    deleteAllFormData,
    deleteAllRefFieldsFormData,
    fetchFormData,
} from "hooks/dataOfForm";
import {
    getFieldsDataById,
} from "auth/actions/objActions";


/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import ImportRecordsByCSV from "./import";
import { LayoutTopSide } from "components/NavBar/styles";
import "./styles.scss";

/**
 * Utils
 */
import buildColumns, {
    buildSchemaColumns,
    getLookupData,
} from "components/Utils/formColumns";
import buildTableData from "components/Utils/formData";
import Notification from "components/Notifications";
import mountRecords from "components/Utils/buildRecords";
import removeUndefined from "components/Utils/removeUndefined";
import { getFieldValueForTable } from "components/Utils/getFieldValue";
import { usePersistentFilters } from "utils/use-persistents-filters";
import { defaultTablePagination } from "utils/table-pagination";
import buildFilterEntryKeepField from "../../../utils/normalization-dynamic-fields";
import useRecordsPermissions from "./hooks/useRecordsPermissions";
import useRecordOperations from "./hooks/useRecordOperations";
import useCSVOperations from "./hooks/useCSVOperations";
import ExportModal from "components/ExportModal";
import { enforceParentChildOrder } from "utils/order-parent-child-fields";
import { clearFormulaFields, mergeFormulaResults, mountRecordsToFormula } from "services/helper/moutRecordsToFormula";

/**
 * Misc
 */

const formItemLayout = {
    labelCol: {
        span: 24,
    },
    wrapperCol: {
        span: 24,
    },
};

// Colunas ocultas por padrão em qualquer formulário.
const SYSTEM_HIDDEN_COLUMNS = {
    _id: { show: false },
    createdAt: { show: false },
    updatedAt: { show: false },
};

const columnsStateStorageKey = (name) => `clm-pro-table-records-v6-${name}`;

// Identifica as colunas de ID: o ID do registro e os IDs gerados para os campos
// de consulta (refLookup), nomeados como "<campo> (ID)".
const isIdColumn = (col) => {
    const key = col?.key ?? col?.dataIndex;
    if (typeof key !== "string") {
        return false;
    }
    return (
        key === "_id" ||
        key.endsWith("(ID)") ||
        (col?.title != null && col.title.toString().endsWith("(ID)"))
    );
};

const isColumnVisible = (columnsState, col) =>
    columnsState?.[col?.key ?? col?.dataIndex]?.show !== false;

const readColumnsState = (name) => {
    const savedState = localStorage.getItem(columnsStateStorageKey(name));
    if (!savedState) {
        return null;
    }
    try {
        return JSON.parse(savedState);
    } catch (e) {
        console.error("Error parsing saved columns state", e);
        return null;
    }
};

// Preserva a escolha explícita do usuário e aplica o padrão apenas às colunas
// que ele ainda não configurou (inclusive as adicionadas depois ao formulário).
const mergeColumnsState = (defaultState, savedState) => {
    if (!savedState) {
        return defaultState;
    }
    const merged = { ...savedState };
    Object.entries(defaultState).forEach(([key, config]) => {
        merged[key] = { ...config, ...(savedState[key] ?? {}) };
    });
    return merged;
};

function RecordForms(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const actionRef = useRef();
    const formRef = useRef()
    const params = new URLSearchParams(location.search);
    const id = params.get("id") || null;
    const { name } = useParams();
    const currentForm = decodeURIComponent(name);
    const isCurrentFormData = props.formData.formName === name;
    const tableFields = useMemo(() => {
        const unique = _.uniqBy(props.formData.fields || [], "field").sort((a, b) => a.index - b.index);
        return enforceParentChildOrder(unique);
    }, [props.formData.fields]);
    const tableColumnsList = useMemo(
        () => tableFields.map((field) => field.field),
        [tableFields]
    );
    const [query, setQuery] = useState({});
    const [sorterBy, setSorterBy] = useState({ createdAt: "descend" });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [checkedRowsData, setCheckedRowsData] = useState([]);
    const [checkedRows, setCheckedRows] = useState([]);
    const [checkedRowsRef, setCheckedRowsRef] = useState({});
    const [loadings, setLoadings] = useState([]);
    const [exportModalVisible, setExportModalVisible] = useState(false); // State for ExportModal

    // Tabela
    const columns = useMemo(
        () => {
            if (!isCurrentFormData) {
                return [];
            }
            const baseColumns = buildColumns(
                tableColumnsList,
                tableFields,
                props.formData.refFields,
                true,
                true
            );

            return baseColumns.map(col => {
                const sortOrder = sorterBy?.[col.dataIndex] || sorterBy?.[col.key];
                if (col.dataIndex === "_id" || (col.key && col.key.endsWith("(ID)"))) {
                    return {
                        ...col,
                        sortOrder: sortOrder ?? undefined,
                        render: (_, entity) => {
                            const rawText = entity[col.dataIndex];
                            const text = (typeof rawText === 'object' && rawText !== null) ? (rawText._id || rawText.value) : rawText;

                            if (!text) return "-";
                            return (
                                <Typography.Link
                                    href={`/atividades/todas?idsAffected=${text}`}
                                    target="_blank"
                                    style={{
                                        color: '#2d7ff9',
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                    }}
                                >
                                    {text}
                                </Typography.Link>
                            );
                        }
                    };
                }
                return {
                    ...col,
                    sortOrder: sortOrder ?? undefined,
                };
            });
        },
        [tableColumnsList, tableFields, props.formData.refFields, navigate, isCurrentFormData, sorterBy]
    );

    const [stableColumns, setStableColumns] = useState([]);
    useEffect(() => {
        if (!isCurrentFormData || columns.length === 0) {
            return;
        }
        if (
            props.formData.total > 0 ||
            stableColumns.length === 0 ||
            Object.keys(sorterBy || {}).length > 0
        ) {
            setStableColumns(columns);
        }
    }, [columns, isCurrentFormData, props.formData.total, stableColumns.length, sorterBy]);

    // Padrão de exibição: colunas de identificador (ID do registro e ID dos campos
    // de consulta) e colunas de sistema começam ocultas.
    const defaultColumnsState = useMemo(() => {
        const defaultState = { ...SYSTEM_HIDDEN_COLUMNS };
        columns.forEach(col => {
            if (isIdColumn(col)) {
                defaultState[col.key ?? col.dataIndex] = { show: false };
            }
        });
        return defaultState;
    }, [columns]);

    const [columnsState, setColumnsState] = useState(() =>
        mergeColumnsState(SYSTEM_HIDDEN_COLUMNS, readColumnsState(name))
    );

    // As colunas de ID dos campos de consulta só existem depois que o formulário
    // é carregado, então os padrões são reaplicados nesse momento.
    const appliedDefaultsFor = useRef(null);
    useEffect(() => {
        if (columns.length === 0 || appliedDefaultsFor.current === name) {
            return;
        }
        appliedDefaultsFor.current = name;
        setColumnsState(mergeColumnsState(defaultColumnsState, readColumnsState(name)));
    }, [columns.length, defaultColumnsState, name]);

    // O formulário de busca do ProTable é montado a partir de todas as colunas
    // recebidas (ele não aplica o columnsMap), então a coluna escondida pela
    // engrenagem precisa vir com "search: false" para o filtro sumir junto.
    const tableColumns = useMemo(() => {
        const baseColumns = stableColumns.length > 0 ? stableColumns : columns;
        return baseColumns.map(col => (
            isColumnVisible(columnsState, col) ? col : { ...col, search: false }
        ));
    }, [stableColumns, columns, columnsState]);

    // Campos cujo filtro está visível, usado para manter o contador de filtros
    // aplicados coerente com o que o usuário enxerga.
    const visibleFilterFields = useMemo(() => {
        const fields = new Set();
        tableColumns.forEach(col => {
            if (col.search !== false) {
                fields.add(col.dataIndex ?? col.title);
            }
        });
        return fields;
    }, [tableColumns]);

    const {
        permissions,
        company,
        permissionsFromForm,
        canInsert: hasPermissionToInsertDataInForm,
        canView: hasPermissionToViewDataFromForm,
        canEdit: hasPermissionToEditDataInForm,
        canImport: hasPermissionToImportDataInForm,
        canDelete: hasPermissionToDeleteDataFromForm,
        canViewArchived: hasPermissionToViewDataArchived,
        canViewFinal: hasPermissionToViewDataFromFormFinal
    } = useRecordsPermissions(name);

    const enterLoading = (index) => {
        setLoadings((prevLoadings) => {
            const newLoadings = [...prevLoadings];
            newLoadings[index] = true;
            return newLoadings;
        });
        setTimeout(() => {
            setLoadings((prevLoadings) => {
                const newLoadings = [...prevLoadings];
                newLoadings[index] = false;
                return newLoadings;
            });
        }, 600);
    };

    // Formulário (adição, edição única e múltipla)
    const recordFormRef = useRef();
    const [recordFormAction, setRecordFormAction] = useState("add");
    const [idRecord, setIdRecord] = useState(null);

    const orderedFormFields = useMemo(() => {
        const fields = props.formData.fields || [];
        const sorted = _.uniqBy(fields, "field").sort((a, b) => a.index - b.index);
        return enforceParentChildOrder(sorted);
    }, [props.formData.fields]);

    const recordFormColumns = useMemo(
        () =>
            buildSchemaColumns(
                orderedFormFields.map((item) => item.field),
                orderedFormFields,
                props.formData.refFields,
                checkedRowsRef,
                recordFormRef,
                recordFormAction,
                null,
                dispatch,
                currentForm,
                idRecord
            ),
        [
            props.formData.columns,
            orderedFormFields,
            props.formData.refFields,
            checkedRowsRef,
        ]
    );

    // Busca quais os fields que deverão ser inputados filtros
    const getFieldsColumns = () => {
        // Se a coluna ou o nome estiver vazio, retorna array vazio
        if (!name || !columns || columns.length === 0) {
            return [];
        }

        const config = columns
            .filter(c => c.search !== false)
            .map(c => buildFilterEntryKeepField(c.dataIndex ?? c.title, c, name));
        return config;
    };

    // Ao ocultar uma coluna pela engrenagem o filtro dela some do formulário, então
    // o valor já persistido é descartado para não filtrar de forma invisível.
    const clearFiltersOfHiddenColumns = useCallback((nextColumnsState) => {
        const urlParams = new URLSearchParams(location.search);
        let hasRemovedParam = false;

        columns.forEach(col => {
            if (col.search === false || isColumnVisible(nextColumnsState, col)) {
                return;
            }
            const entry = buildFilterEntryKeepField(col.dataIndex ?? col.title, col, name);
            const queryParams = entry.type === "dateRange"
                ? [entry.queryParamStart, entry.queryParamEnd]
                : [entry.queryParam];
            const localKeys = entry.type === "dateRange"
                ? [entry.localKeyStart, entry.localKeyEnd]
                : [entry.localKey];

            queryParams.forEach(queryParam => {
                if (urlParams.has(queryParam)) {
                    urlParams.delete(queryParam);
                    hasRemovedParam = true;
                }
            });
            localKeys.forEach(localKey => localStorage.removeItem(localKey));
            formRef.current?.setFieldsValue?.({ [entry.field]: undefined });
        });

        if (hasRemovedParam) {
            setCurrentPage(1);
            navigate(
                { pathname: location.pathname, search: urlParams.toString() },
                { replace: true }
            );
        }
    }, [columns, location, name, navigate]);

    // useState para definir se os dados estão o
    const [columnIsOk, setColumnsIsOk] = useState(false);

    // useState para mudar os fields após a conclusão do carregamento da tela
    const [fieldsColumn, setFieldsColumns] = useState([]);

    // useEffect para notificar que os dados estão ok, e mudar os fields da tela
    useEffect(() => {
        if (
            !props.formData.loading &&
            props.formData.fields?.length > 0
        ) {
            setFieldsColumns(getFieldsColumns());
            setColumnsIsOk(true);
        }
    }, [props.formData.loading, props.formData.fields]);


    useEffect(() => {
        if (!hasPermissionToViewDataFromFormFinal) return;

        dispatch(
            fetchFormData(
                name,
                currentPage,
                pageSize,
                null,
                { ...query, id },
                sorterBy,
                location,
                navigate,
                formRef,
                true
            )
        );
    }, [
        name,
        currentPage,
        pageSize,
        query,
        sorterBy,
        hasPermissionToViewDataFromFormFinal,
        dispatch,
        location,
        navigate
    ]);

    // Hook personalizado para aplicar filtros persistentes na tela
    const { onReset, onSubmitFilters } = usePersistentFilters(fieldsColumn, actionRef, formRef, location, navigate, name, columnIsOk, id, true);

    let recordFormColumnsFinal;
    if (recordFormAction !== "multipleEdit") {
        recordFormColumnsFinal = recordFormColumns;
    } else {
        const recordFormColumnsTemp = _.cloneDeep(recordFormColumns);
        recordFormColumnsFinal = recordFormColumnsTemp.map(field => {
            field.initialValue = undefined;
            if (_.isObject(field.formItemProps)) {
                if (field.formItemProps.hasOwnProperty('required')) {
                    field.formItemProps.required = false;
                }
            }
            return field;
        });

        recordFormColumnsFinal.push({
            key: "acceptEmptyValues",
            name: "acceptEmptyValues",
            title: "Considerar valores vazios para atualização de campos dos registros selecionado?",
            valueType: "switch"
        })
    }

    // Adição
    const [openForm, setOpenForm] = useState(false);

    // Hooks
    const {
        handleDelete,
        handleSave,
        loadingSend,
        setLoadingSend,
        checkIfIsEmpty
    } = useRecordOperations({
        name,
        user: props.user,
        checkedRows,
        setOpenForm,
        fields: props.formData.fields,
        setCheckedRowsData,
        refreshTable: () => {
            dispatch(
                fetchFormData(
                    name,
                    currentPage,
                    pageSize,
                    null,
                    query,
                    sorterBy,
                    location,
                    navigate,
                    formRef,
                    true
                )
            );
            actionRef.current?.clearSelected();
        }
    });

    const {
        importRecords,
        disableImport,
        resetUploader,
        queueId,
        openImport,
        closeImport,
        handleImportFileUpload,
        handleExportModel,
        handleExportCSV,
        exportLoading,
        exportQueueId,
        exportResult
    } = useCSVOperations({
        name,
        fields: props.formData.fields,
        user: props.user,
        setExportModalVisible, // Pass setter
        query,
        sorterBy,
        location,
        navigate,
        formRef,
        isRegistryForm: true,
        totalRecords: isCurrentFormData ? props.formData.total : 0
    });

    const utilsItemsTable = useMemo(() => [
        ...(
            hasPermissionToImportDataInForm ? [
                {
                    key: "1",
                    label: "Importar",
                    onClick: openImport,
                },
                {
                    key: "2",
                    label: "Exportar modelo",
                    onClick: handleExportModel,
                },] : []
        ),
        {
            key: "3",
            label: "Exportar CSV",
            onClick: handleExportCSV,
        },
    ], [hasPermissionToImportDataInForm, openImport, handleExportModel, handleExportCSV]);

    const dataSource = useMemo(() => {
        if (hasPermissionToViewDataFromFormFinal && isCurrentFormData) {
            return buildTableData(props.formData.records, tableFields);
        }
        return [];
    }, [hasPermissionToViewDataFromFormFinal, isCurrentFormData, props.formData.records, tableFields]);
    const totalRecords = isCurrentFormData ? props.formData.total : 0;
    const totalInIndexRecords = isCurrentFormData ? props.formData.totalInIndex : 0;
    const createdAtFilterValue = formRef.current?.getFieldValue?.("createdAt");
    const hasCreatedAtFilter = _.isArray(createdAtFilterValue)
        ? createdAtFilterValue.some((value) => !_.isNil(value))
        : !_.isNil(createdAtFilterValue);
    const hasActiveFilters = !!id || (location.search && location.search !== "?") || hasCreatedAtFilter;
    const displayTotalRecords = hasActiveFilters ? totalRecords : totalInIndexRecords;

    // Conta os filtros aplicados (persistidos na URL pelo usePersistentFilters),
    // para exibir no badge quando o painel de filtros estiver recolhido
    const searchParamsForCount = new URLSearchParams(location.search);
    let createdAtCounted = false;
    let activeFilterCount = fieldsColumn.reduce((count, entry) => {
        if (!visibleFilterFields.has(entry.field)) return count;
        const isActive = entry.type === "dateRange"
            ? !!(searchParamsForCount.get(entry.queryParamStart) || searchParamsForCount.get(entry.queryParamEnd))
            : !!searchParamsForCount.get(entry.queryParam);
        if (isActive && entry.field === "createdAt") createdAtCounted = true;
        return isActive ? count + 1 : count;
    }, 0);
    if (id) activeFilterCount += 1;
    if (!createdAtCounted && hasCreatedAtFilter && visibleFilterFields.has("createdAt")) activeFilterCount += 1;
    const filterCountTooltip =
        activeFilterCount === 0
            ? null
            : activeFilterCount === 1
                ? "1 filtro aplicado"
                : `${activeFilterCount} filtros aplicados`;
    const showNoResultsMessage =
        isCurrentFormData &&
        !props.formData.loading &&
        hasActiveFilters &&
        totalRecords === 0;

    return (
        <LayoutTopSide>
            <TopBar />
            <Layout style={{ overflow: "hidden" }}>
                <DrawerSider />
                <Layout
                    className="records-container"
                    style={{
                        padding: "0 24px 24px",
                        height: "100%",
                        overflowY: "scroll",
                    }}
                >
                    <PageHeader
                        style={{ margin: "4px 0px 5px", paddingLeft: 0 }}
                        title={`Registros do formulário: ${currentForm.replace("obj_", "")}`}
                        onBack={() => window.history.back()}
                    />
                    <div className="table-wrapper">
                        <ConfigProvider locale={ptBRIntl}>
                            <ProTable
                                key={name}
                                formRef={formRef}
                                actionRef={actionRef}
                                rowKey="_id"
                                type="table"
                                onSubmit={(values) => {
                                    setCurrentPage(1);
                                    onSubmitFilters(values);
                                }}
                                onReset={() => {
                                    setCurrentPage(1);
                                    onReset();
                                }}
                                loading={props.formData.loading || exportLoading}
                                columns={tableColumns}
                                dataSource={hasPermissionToViewDataFromFormFinal ? dataSource : []}
                                tableRender={(_, dom, domList) => {
                                    const { table, toolbar, search, alert } = domList || {};
                                    if (props.formData.loading) {
                                        return (
                                            <div className="records-loading">
                                                <Spin size="large" />
                                            </div>
                                        );
                                    }
                                    return (
                                        <>
                                            {search}
                                            {toolbar}
                                            {alert}
                                            {showNoResultsMessage ? (
                                                <Empty description="Sem registro para a pesquisa solicitada." />
                                            ) : (
                                                table
                                            )}
                                        </>
                                    );
                                }}
                                onChange={(_, __, sorter) => {
                                    if (!sorter || !sorter.field || !sorter.order) {
                                        setSorterBy({});
                                        return;
                                    }

                                    setSorterBy({
                                        [sorter.field]: sorter.order,
                                    });
                                }}
                                toolbar={{
                                    actions: [
                                        hasPermissionToViewDataArchived ? (<Button
                                            type="link"
                                            onClick={() =>
                                                navigate(`/formularios/lixeira/${name}`)
                                            }
                                        >
                                            Arquivados
                                        </Button>) : null,
                                        hasPermissionToInsertDataInForm ? (
                                            <Button
                                                type="primary"
                                                loading={loadings[1]}
                                                onClick={async () => {
                                                    if (
                                                        props.formData.total === 0 &&
                                                        props.formData.columns.length === 0
                                                    ) {
                                                        notification['info']({
                                                            message: "Você precisa adicionar novos campos ao formulário antes de adicionar um novo registro."
                                                        })
                                                    } else {
                                                        // Limpa os campos referenciados e lookups do Redux
                                                        dispatch(deleteAllRefFieldsFormData());
                                                        // Limpa os campos de retorno do Redux
                                                        dispatch(deleteAllFormData());
                                                        // Limpa a referência de linhas selecionadas
                                                        setCheckedRowsRef({});
                                                        // Limpa os campos
                                                        recordFormRef.current?.resetFields();
                                                        // Espera alguns instantes
                                                        enterLoading(1);
                                                        // Ação (CRUD)
                                                        setRecordFormAction("add");
                                                        // Abre formulário
                                                        setOpenForm(true);
                                                    }
                                                }}
                                                disabled={
                                                    props.formData.loading || checkedRows.length > 0
                                                }
                                            >
                                                Adicionar
                                            </Button>) : null
                                        ,
                                        <Dropdown.Button
                                            menu={{ items: utilsItemsTable }}
                                            disabled={props.formData.loading}
                                        >
                                            Ações
                                        </Dropdown.Button>,
                                    ],
                                }}
                                pagination={{
                                    ...defaultTablePagination,
                                    pageSize,
                                    total: totalRecords,
                                    current: currentPage,
                                    onChange: (page, pageSize) => {
                                        setCurrentPage(page);
                                        setPageSize(pageSize);
                                    },
                                }}
                                headerTitle={`${displayTotalRecords} ${displayTotalRecords === 1 ? "registro" : "registros"
                                    } ${displayTotalRecords === 1 ? "encontrado" : "encontrados"
                                    }`}
                                columnsState={{
                                    value: columnsState,
                                    defaultValue: defaultColumnsState,
                                    onChange: (map) => {
                                        clearFiltersOfHiddenColumns(map);
                                        setColumnsState(map);
                                        localStorage.setItem(columnsStateStorageKey(name), JSON.stringify(map));
                                    }
                                }}
                                options={{
                                    reload: () => {
                                        dispatch(
                                            fetchFormData(
                                                name,
                                                currentPage,
                                                pageSize,
                                                null,
                                                query,
                                                sorterBy,
                                                location,
                                                navigate,
                                                formRef,
                                                true
                                            )
                                        );
                                        actionRef.current?.clearSelected();
                                    },
                                }}
                                tableAlertRender={useCallback(({
                                    selectedRowKeys,
                                    selectedRows,
                                    onCleanSelected,
                                }) => {
                                    return (
                                        <Space size={24}>
                                            <span>
                                                Selecionado {selectedRowKeys.length} registros
                                                <a
                                                    style={{ marginInlineStart: 8 }}
                                                    onClick={() => {
                                                        onCleanSelected();
                                                    }}
                                                >
                                                    cancelar seleção
                                                </a>
                                            </span>
                                        </Space>
                                    );
                                }, [])}
                                tableAlertOptionRender={useCallback(() => {
                                    return (
                                        <Space size={16}>
                                            {
                                                hasPermissionToEditDataInForm ? (<a
                                                    href={() => false}
                                                    onClick={async () => {
                                                        // Limpa os campos
                                                        recordFormRef.current?.resetFields();
                                                        // Limpa os campos referenciados e lookups do Redux
                                                        dispatch(deleteAllRefFieldsFormData());
                                                        // Limpa os campos de retorno do Redux
                                                        dispatch(deleteAllFormData());
                                                        // Limpa a referência de linhas selecionadas
                                                        setCheckedRowsRef({});
                                                        // Verifica se é seleção única ou múltipla
                                                        if (checkedRows.length > 1) {
                                                            setRecordFormAction("multipleEdit");
                                                            // Força a limpeza dos campos para edição em massa
                                                            const emptyValues = {};
                                                            if (props.formData && props.formData.fields) {
                                                                props.formData.fields.forEach(f => {
                                                                    emptyValues[f.field] = null;
                                                                });
                                                            }
                                                            recordFormRef.current?.setFieldsValue(emptyValues);
                                                            // Abre formulário
                                                            setOpenForm(true);
                                                        } else {
                                                            // Ação (CRUD)
                                                            setRecordFormAction("edit");
                                                            setIdRecord(checkedRows[0]._id);
                                                            // Tenta obter o registro
                                                            const nativeSelectedRecord =
                                                                await getFieldsDataById({
                                                                    table: currentForm.replace("obj_", ""),
                                                                    id: checkedRows[0]._id,
                                                                });
                                                            const { message: selectedRecordMessage } = nativeSelectedRecord;
                                                            // Verifica se encontrou o registro selecionado
                                                            if (selectedRecordMessage) {
                                                                // Salva o registro em cache para ser utilizado em mountRecords()
                                                                setCheckedRowsData(selectedRecordMessage);
                                                            }
                                                            // Procura por campos referenciados e lookups
                                                            const onlyRefLkpFields =
                                                                props.formData.fields.filter(
                                                                    (e) =>
                                                                        e.fieldtype === "refLookup" ||
                                                                        e.fieldtype === "refUniqueSelection" ||
                                                                        e.fieldtype === "refMultipleSelection"
                                                                );
                                                            const onlyLkpFields =
                                                                props.formData.fields.filter(
                                                                    (e) => e.fieldtype === "refLookup"
                                                                ) || [];
                                                            // Remove campos referenciados e lookups
                                                            const withoutRefLkp =
                                                                _.omit(
                                                                    selectedRecordMessage,
                                                                    _.map(onlyRefLkpFields, "field")
                                                                ) || {};
                                                            // Verifica se registro não foi excluído
                                                            if (
                                                                selectedRecordMessage &&
                                                                !_.isEmpty(selectedRecordMessage)
                                                            ) {
                                                                const onlyRefLkp = _.pick(
                                                                    selectedRecordMessage,
                                                                    _.map(onlyRefLkpFields, "field")
                                                                );
                                                                const transformedOnlyRefLkp = {};
                                                                // Intera sobre os campos referenciados e lookups
                                                                for (const key in onlyRefLkp) {
                                                                    if (onlyRefLkp.hasOwnProperty(key)) {
                                                                        if (onlyRefLkp[key] && onlyRefLkp[key].length > 0) {
                                                                            const genId = `${onlyRefLkp[key]
                                                                                .map((obj) => obj._id)
                                                                                .join(",")}_rng@record_${uuid()}`;
                                                                            const currentValue = onlyRefLkp[key]
                                                                                .map((obj) => {
                                                                                    let temp = obj.value;
                                                                                    if (moment(temp, "YYYY-MM-DDTHH:mm:ss[Z]", true).isValid()) {
                                                                                        temp = moment(temp, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
                                                                                    }
                                                                                    return temp;
                                                                                })
                                                                                .join(", ");


                                                                            let currentValueDate = null;
                                                                            if (moment(currentValue, "YYYY-MM-DDTHH:mm:ss[Z]", true).isValid()) {
                                                                                currentValueDate = moment(currentValue, "YYYY-MM-DDTHH:mm:ss[Z]", true).format("DD/MM/YYYY")
                                                                            }

                                                                            // Verifica se existe valor no campo
                                                                            if (!genId.startsWith("_r")) {
                                                                                // Extrai as propriedades _id e value, une com vírgulas e armazena
                                                                                transformedOnlyRefLkp[key] = [
                                                                                    {
                                                                                        _id: genId,
                                                                                        value: currentValueDate || currentValue,
                                                                                    },
                                                                                ];
                                                                                dispatch(
                                                                                    addRefFieldsFormData(key, [
                                                                                        {
                                                                                            value: genId,
                                                                                            label: currentValue,
                                                                                        },
                                                                                    ])
                                                                                );
                                                                                // Tenta preencher o formulário com os registros de campos referenciados e lookups
                                                                                recordFormRef.current?.setFieldsValue({
                                                                                    [key]: genId,
                                                                                });
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                                // Prepara o cache para os campos referenciados e lookups
                                                                setCheckedRowsRef(transformedOnlyRefLkp);
                                                                // Prepara os dados para ser preenchido na tabela
                                                                let recordForEdit = getFieldValueForTable(
                                                                    [withoutRefLkp],
                                                                    _.concat(
                                                                        props.formData.fields,
                                                                        props.formData.refFields
                                                                    ).filter((y) => y.isReadOnly === undefined)
                                                                );
                                                                const dateFields = props.formData.fields.filter((e) => e.fieldtype === "Date") || []

                                                                for (const dateField of dateFields) {
                                                                    if (recordForEdit[dateField.field] && moment(recordForEdit[dateField.field], 'DD/MM/YYYY', true).isValid()) {
                                                                        recordForEdit[dateField.field] = moment(recordForEdit[dateField.field], 'DD/MM/YYYY', true);
                                                                    } else {
                                                                        recordForEdit[dateField.field] = null;
                                                                    }
                                                                }

                                                                // Tenta preencher os campos simples
                                                                recordFormRef.current?.setFieldsValue(
                                                                    // Remove campos referenciados e lookups
                                                                    _.omit(
                                                                        recordForEdit,
                                                                        _.map(onlyRefLkpFields, "field")
                                                                    )
                                                                );
                                                            }
                                                            // Abre o formulário imediatamente; o carregamento dos
                                                            // campos de retorno acontece em seguida, exibindo o
                                                            // loading sobre o modal já aberto.
                                                            setOpenForm(true);
                                                            // Verifica se existe campos de lookups e carrega os
                                                            // campos de retorno. Enquanto carregam, o loading fica
                                                            // visível e o botão de salvar permanece bloqueado,
                                                            // evitando salvar o registro com os campos filhos vazios.
                                                            if (
                                                                selectedRecordMessage &&
                                                                !_.isEmpty(selectedRecordMessage) &&
                                                                onlyLkpFields &&
                                                                onlyLkpFields.length > 0
                                                            ) {
                                                                for (const obj of onlyLkpFields) {
                                                                    if (
                                                                        selectedRecordMessage[obj.field] &&
                                                                        selectedRecordMessage[obj.field].length > 0 &&
                                                                        selectedRecordMessage[obj.field][0]._id !== ""
                                                                    ) {
                                                                        // Obtém os dados do campo de retorno e preenche o formulário
                                                                        await getLookupData({
                                                                            tableRef: recordFormRef,
                                                                            selectedField: obj,
                                                                            selectedOpts: `${selectedRecordMessage[obj.field][0]._id}_rng@record_${uuid()}`,
                                                                            dispatch,
                                                                        });
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                >
                                                    Editar
                                                </a>) : null
                                            }
                                            {
                                                hasPermissionToDeleteDataFromForm ? (
                                                    <a href={() => false} onClick={handleDelete}>
                                                        Excluir
                                                    </a>) : null
                                            }

                                        </Space>
                                    );
                                }, [hasPermissionToEditDataInForm, checkedRows, checkedRowsRef, currentForm, dispatch, props.formData.fields, props.formData.refFields, hasPermissionToDeleteDataFromForm, handleDelete])}
                                locale={{
                                    emptyText: (
                                        <Empty description="Nenhum registro adicionado." />
                                    ),
                                    triggerDesc: "Ordem decrescente",
                                    triggerAsc: "Ordem crescente",
                                }}
                                dateFormatter={"number"}
                                scroll={{ x: 'max-content' }}
                                rowSelection={{
                                    onChange: (selectedRowKeys, selectedRows) => {
                                        setCheckedRows(selectedRows);
                                    }
                                }}
                                search={{
                                    layout: "vertical",
                                    ...formItemLayout,
                                    // Mostra em vermelho a quantidade de filtros aplicados
                                    // enquanto o painel está recolhido (mesmo padrão da
                                    // tela de atividades de dados)
                                    collapseRender: (collapsed) => (
                                        <Tooltip title={collapsed ? filterCountTooltip : null}>
                                            <Badge
                                                count={collapsed ? activeFilterCount : 0}
                                                size="small"
                                                offset={[10, -2]}
                                            >
                                                <Space size={4}>
                                                    {collapsed ? "Expandir" : "Recolher"}
                                                    <DownOutlined rotate={collapsed ? 0 : 180} />
                                                </Space>
                                            </Badge>
                                        </Tooltip>
                                    ),
                                }}
                            />
                            {/* Formulário */}
                            <BetaSchemaForm
                                formRef={recordFormRef}
                                open={openForm}
                                layoutType={"ModalForm"}
                                modalProps={{
                                    // pro-components v2: o SchemaForm so gera os campos se o
                                    // form interno ja montou (formRef.current). Sem forceRender
                                    // o form montaria apenas na 1a abertura, tarde demais, e o
                                    // modal abriria vazio (no v1 o form montava com a pagina).
                                    forceRender: true,
                                    onCancel: async () => {
                                        // Limpa os campos referenciados e lookups
                                        dispatch(deleteAllRefFieldsFormData());
                                        // Limpa os campos de retorno
                                        dispatch(deleteAllFormData());
                                        if (recordFormAction === "edit" || recordFormAction === "multipleEdit") {
                                            // Limpa seleção do registro para edição
                                            setCheckedRowsData([]);
                                            setCheckedRowsRef({});
                                        }
                                        // Fecha o formulário
                                        setOpenForm(false);
                                        setRecordFormAction("add");
                                        setIdRecord(null)
                                    },
                                    okText: "Salvar registro",
                                }}
                                columns={recordFormColumnsFinal}
                                labelCol={{ span: 24 }}
                                submitter={{
                                    searchConfig: {
                                        submitText: "Salvar registro",
                                    },
                                    submitButtonProps: {
                                        // Bloqueia o salvamento enquanto os campos de
                                        // retorno (lookup) ainda estão carregando, evitando
                                        // que o registro seja salvo com os campos filhos vazios.
                                        disabled: props.formData.lkpLoading > 0,
                                        loading: props.formData.lkpLoading > 0,
                                    },
                                }}
                                onFinish={async (formValues) => {
                                    setLoadingSend(true)
                                    // Mantém os dados do formulário em cache
                                    let submitedData = formValues;
                                    // Filtra campos por tipo
                                    const onlyFieldsName =
                                        _.map(
                                            props.formData.fields.filter(
                                                (e) =>
                                                    e.fieldtype === "Text" ||
                                                    e.fieldtype === "Numeric" ||
                                                    e.fieldtype === "Date" ||
                                                    e.fieldtype === "Time" ||
                                                    e.fieldtype === "uniqueSelection" ||
                                                    e.fieldtype === "multipleSelection" ||
                                                    e.fieldtype === "refUniqueSelection" ||
                                                    e.fieldtype === "refMultipleSelection" ||
                                                    e.fieldtype === "refLookup" ||
                                                    e.fieldtype === "formula"
                                            ),
                                            "field"
                                        ) || [];

                                    // Verifica se existe campos no formulário
                                    if (onlyFieldsName && onlyFieldsName.length > 0) {
                                        // Se encontrar tenta fixar erro do onClear()
                                        onlyFieldsName.forEach((item) => {
                                            // Verifica se o campo não existe
                                            if (!submitedData.hasOwnProperty(item)) {
                                                // Adiciona campo com valor vazio
                                                submitedData[item] = null;
                                            }
                                        });
                                    }
                                    Object.keys(checkedRowsData).forEach(field => {
                                        const value = checkedRowsData[field];

                                        if (!Array.isArray(value) || value.length === 0) {
                                            checkedRowsData[field] = [{
                                                _id: uuid(),
                                                value: null
                                            }];
                                        }
                                    });

                                    // Tenta converter os dados para API
                                    const result = await mountRecords(
                                        currentForm,
                                        submitedData,
                                        props.formData.fields,
                                        props.formData.refValues,
                                        props.formData.lkpValues,
                                        checkedRowsData,
                                        recordFormAction
                                    );
                                    // Remove campos vazios
                                    const clearedResult = clearFormulaFields(result, props.formData.fields);
                                    const resultToFormula = await mountRecordsToFormula(
                                        currentForm,
                                        result,
                                        props.formData.fields
                                    );

                                    const updatedResult = mergeFormulaResults(
                                        clearedResult,
                                        resultToFormula
                                    );
                                    let finalResult = removeUndefined(_.assign({}, ...updatedResult)) || {};

                                    // Tenta enviar dados para API
                                    const response = await handleSave(finalResult, recordFormAction);
                                    if (!response) {
                                        message.error('Houve um problema ao realizar a requisição para validação dos campos.');
                                        setLoadingSend(false);
                                        return;
                                    }

                                    if (!response?.success && response.type === 'field') {
                                        setLoadingSend(false);

                                        const fields = _(response.fields)
                                            .groupBy('fieldName')
                                            .map((items, name) => ({
                                                name,
                                                errors: items.map(i => i.errorField)
                                            }))
                                            .value();

                                        // Limpa erros anteriores de todos os campos do formulário
                                        const clearErrors = orderedFormFields.map(f => ({
                                            name: f.field,
                                            errors: []
                                        }));
                                        recordFormRef.current?.setFields(clearErrors);

                                        if (fields.length === 0) {
                                            message.error(response.error);
                                            return;
                                        }
                                        recordFormRef.current?.setFields(fields);
                                        const messageErrorToShow = fields.length > 1
                                            ? `Você possuí ${fields.length} erros de validação. Realize as correções para prosseguir.`
                                            : fields?.[0].errors.length > 1
                                                ? `O campo '${fields?.[0]?.name}' possui ${fields?.[0]?.errors.length} erros de validação.`
                                                : fields?.[0]?.errors?.[0]
                                        message.error(messageErrorToShow);
                                        return;
                                    }
                                    // Limpa os campos referenciados e lookups do Redux
                                    dispatch(deleteAllRefFieldsFormData());
                                    // Limpa os campos de retorno do Redux
                                    dispatch(deleteAllFormData());

                                    setLoadingSend(false);
                                    setCheckedRowsData([]);
                                    setCheckedRowsRef({});
                                    setIdRecord(null);
                                    actionRef.current?.reload();
                                }}
                            />
                        </ConfigProvider>
                    </div>
                </Layout>
                {/* Exportação */}
                <ExportModal
                    visible={exportModalVisible}
                    queueId={exportQueueId}
                    exportResult={exportResult}
                    onClose={() => setExportModalVisible(false)}
                />
                {/* Importação */}
                <ImportRecordsByCSV
                    importRecords={importRecords}
                    closeImport={closeImport}
                    disableImport={disableImport}
                    resetUploader={resetUploader}
                    handleImportFileUpload={handleImportFileUpload}
                />
                <Notification />
            </Layout>
            {/* Loading exibido ao selecionar um registro pai de um campo de
                consulta (lookup), enquanto os campos de retorno (filhos) são
                carregados. Bloqueia a interação até o carregamento terminar,
                tanto na adição quanto na edição. */}
            {props.formData.lkpLoading > 0 && (
                <div className="lookup-loading-overlay">
                    <Spin size="large" tip="Carregando campos de retorno..." />
                </div>
            )}
        </LayoutTopSide>
    );
}

const mapStateToProps = (all) => {
    return {
        user: all.session.user,
        formData: all.records,
    };
};

export default connect(mapStateToProps)(RecordForms);
